import axios from "axios";
import { API_BASE } from "./backendUrl";

/* =========================================================
   STORAGE KEYS
   ========================================================= */

const ADMIN_TOKEN_KEY = "pps_admin_token";
const ADMIN_USER_KEY = "pps_admin_user";

export const UNAUTHORISED_MESSAGE =
  "You are Not Authorised for this action. Contact Admin.";

/* =========================================================
   HTTP CLIENT
   ========================================================= */

export const adminHttp = axios.create({
  baseURL: API_BASE,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

/* =========================================================
   REQUEST INTERCEPTOR
   ========================================================= */

adminHttp.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);

    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/* =========================================================
   RESPONSE INTERCEPTOR
   ========================================================= */

adminHttp.interceptors.response.use(
  (response) => {
    return response;
  },

  (error) => {
    const status = error?.response?.status;

    /* -------------------------------------------------------
       403 - NOT AUTHORISED

       IMPORTANT:
       403 par logout nahi hoga.
       403 par login page par redirect nahi hoga.
       ------------------------------------------------------- */

    if (status === 403) {
      const detail = error?.response?.data?.detail;

      let message = UNAUTHORISED_MESSAGE;

      if (
        typeof detail === "string" &&
        detail.trim()
      ) {
        message = detail;
      }

      error.userMessage = message;
    }

    /* -------------------------------------------------------
       409 - ACTIVE SESSION

       AdminLogin.jsx is error ko pakad kar popup dikhayega.
       Yahan logout / redirect nahi karna hai.
       ------------------------------------------------------- */

    if (status === 409) {
      const detail = error?.response?.data?.detail;

      error.userMessage =
        typeof detail === "string" &&
        detail.trim()
          ? detail
          : "User is already logged in on another window/device.";

      error.isSessionConflict = true;

      return Promise.reject(error);
    }

    /* -------------------------------------------------------
       401 - SESSION EXPIRED / INVALID SESSION

       Login API ke 401 ko yahan blindly redirect nahi karna.
       Agar request login/forgot/reset ki hai to caller ko
       actual backend error dikhane dena hai.
       ------------------------------------------------------- */

    if (status === 401) {
      const requestUrl =
        error?.config?.url || "";

      const isAuthRequest =
        requestUrl.includes("/api/auth/login") ||
        requestUrl.includes("/api/auth/refresh") ||
        requestUrl.includes("/api/auth/forgot-password") ||
        requestUrl.includes("/api/auth/reset-password");

      if (!isAuthRequest) {
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        localStorage.removeItem(ADMIN_USER_KEY);

        const currentPath =
          window.location.pathname;

        const isLoginPage =
          currentPath.startsWith(
            "/admin/login"
          ) ||
          currentPath.startsWith(
            "/admin/forgot-password"
          ) ||
          currentPath.startsWith(
            "/admin/reset-password"
          );

        if (!isLoginPage) {
          window.location.href =
            "/admin/login";
        }
      }
    }

    return Promise.reject(error);
  }
);

/* =========================================================
   ERROR FORMATTER
   ========================================================= */

export function formatApiError(error) {
  if (!error) {
    return "Something went wrong";
  }

  /* -------------------------------------------------------
     403
     ------------------------------------------------------- */

  if (
    error?.response?.status === 403
  ) {
    return (
      error?.userMessage ||
      UNAUTHORISED_MESSAGE
    );
  }

  /* -------------------------------------------------------
     409 - ACTIVE SESSION
     ------------------------------------------------------- */

  if (
    error?.response?.status === 409
  ) {
    return (
      error?.userMessage ||
      "User is already logged in on another window/device."
    );
  }

  /* -------------------------------------------------------
     401
     ------------------------------------------------------- */

  if (
    error?.response?.status === 401
  ) {
    const detail =
      error?.response?.data?.detail;

    if (
      typeof detail === "string" &&
      detail.trim()
    ) {
      return detail;
    }

    if (Array.isArray(detail)) {
      const messages = detail
        .map((item) => {
          if (
            typeof item === "string"
          ) {
            return item;
          }

          return item?.msg || "";
        })
        .filter(Boolean);

      if (messages.length > 0) {
        return messages.join(", ");
      }
    }

    return "Session expired. Please login again.";
  }

  /* -------------------------------------------------------
     BACKEND STRING DETAIL
     ------------------------------------------------------- */

  const detail =
    error?.response?.data?.detail;

  if (
    typeof detail === "string" &&
    detail.trim()
  ) {
    return detail;
  }

  /* -------------------------------------------------------
     FASTAPI VALIDATION ERRORS
     ------------------------------------------------------- */

  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (
          typeof item === "string"
        ) {
          return item;
        }

        return item?.msg || "";
      })
      .filter(Boolean);

    if (messages.length > 0) {
      return messages.join(", ");
    }
  }

  /* -------------------------------------------------------
     OBJECT DETAIL
     ------------------------------------------------------- */

  if (
    detail &&
    typeof detail === "object"
  ) {
    if (
      typeof detail.message === "string"
    ) {
      return detail.message;
    }

    if (
      typeof detail.msg === "string"
    ) {
      return detail.msg;
    }
  }

  /* -------------------------------------------------------
     USER MESSAGE
     ------------------------------------------------------- */

  if (
    typeof error?.userMessage === "string" &&
    error.userMessage.trim()
  ) {
    return error.userMessage;
  }

  /* -------------------------------------------------------
     AXIOS ERROR MESSAGE
     ------------------------------------------------------- */

  if (
    typeof error?.message === "string" &&
    error.message.trim()
  ) {
    return error.message;
  }

  return "Something went wrong";
}

/* =========================================================
   AUTH API
   ========================================================= */

export const authApi = {
  /* -------------------------------------------------------
     NORMAL LOGIN
     ------------------------------------------------------- */

  login: async (
    email,
    password
  ) => {
    const response =
      await adminHttp.post(
        "/api/auth/login",
        {
          email,
          password,
        }
      );

    const data =
      response.data;

    if (data?.access_token) {
      localStorage.setItem(
        ADMIN_TOKEN_KEY,
        data.access_token
      );
    }

    if (data?.user) {
      localStorage.setItem(
        ADMIN_USER_KEY,
        JSON.stringify(
          data.user
        )
      );
    }

    return data;
  },

  /* -------------------------------------------------------
     FORCE LOGIN

     Existing active session ko revoke karke
     new session create karega.
     ------------------------------------------------------- */

  forceLogin: async (
    email,
    password
  ) => {
    const response =
      await adminHttp.post(
        "/api/auth/login/force",
        {
          email,
          password,
        }
      );

    const data =
      response.data;

    if (data?.access_token) {
      localStorage.setItem(
        ADMIN_TOKEN_KEY,
        data.access_token
      );
    }

    if (data?.user) {
      localStorage.setItem(
        ADMIN_USER_KEY,
        JSON.stringify(
          data.user
        )
      );
    }

    return data;
  },

  /* -------------------------------------------------------
     LOGOUT
     ------------------------------------------------------- */

  logout: async () => {
    try {
      await adminHttp.post(
        "/api/auth/logout"
      );
    } catch (error) {
      /*
       * Server session already invalid ho sakta hai.
       * Local session phir bhi clear karna hai.
       */
    }

    localStorage.removeItem(
      ADMIN_TOKEN_KEY
    );

    localStorage.removeItem(
      ADMIN_USER_KEY
    );
  },

  /* -------------------------------------------------------
     CLEAR LOCAL SESSION
     ------------------------------------------------------- */

  clearSession: () => {
    localStorage.removeItem(
      ADMIN_TOKEN_KEY
    );

    localStorage.removeItem(
      ADMIN_USER_KEY
    );
  },

  /* -------------------------------------------------------
     TOKEN
     ------------------------------------------------------- */

  hasToken: () => {
    return Boolean(
      localStorage.getItem(
        ADMIN_TOKEN_KEY
      )
    );
  },

  getToken: () => {
    return localStorage.getItem(
      ADMIN_TOKEN_KEY
    );
  },

  /* -------------------------------------------------------
     CACHED USER
     ------------------------------------------------------- */

  getCachedUser: () => {
    try {
      const raw =
        localStorage.getItem(
          ADMIN_USER_KEY
        );

      if (!raw) {
        return null;
      }

      return JSON.parse(raw);
    } catch (error) {
      return null;
    }
  },

  /* -------------------------------------------------------
     SET CACHED USER
     ------------------------------------------------------- */

  setCachedUser: (user) => {
    if (!user) {
      localStorage.removeItem(
        ADMIN_USER_KEY
      );

      return;
    }

    localStorage.setItem(
      ADMIN_USER_KEY,
      JSON.stringify(user)
    );
  },

  /* -------------------------------------------------------
     REFRESH / ME
     ------------------------------------------------------- */

  refreshMe: async () => {
    const response =
      await adminHttp.get(
        "/api/auth/me"
      );

    const user =
      response.data;

    localStorage.setItem(
      ADMIN_USER_KEY,
      JSON.stringify(user)
    );

    return user;
  },
};

/* =========================================================
   ADMIN API
   ========================================================= */

export const adminApi = {
  /* =======================================================
     USERS
     ======================================================= */

  listUsers: async (
    params = {}
  ) => {
    const response =
      await adminHttp.get(
        "/api/admin/users",
        {
          params,
        }
      );

    return response.data;
  },

  getUser: async (
    id
  ) => {
    const response =
      await adminHttp.get(
        `/api/admin/users/${encodeURIComponent(
          id
        )}`
      );

    return response.data;
  },

  createUser: async (
    payload
  ) => {
    const response =
      await adminHttp.post(
        "/api/admin/users",
        payload
      );

    return response.data;
  },

  updateUser: async (
    id,
    payload
  ) => {
    const response =
      await adminHttp.put(
        `/api/admin/users/${encodeURIComponent(
          id
        )}`,
        payload
      );

    return response.data;
  },

  deleteUser: async (
    id
  ) => {
    const response =
      await adminHttp.delete(
        `/api/admin/users/${encodeURIComponent(
          id
        )}`
      );

    return response.data;
  },

  resetUserPassword: async (
    id,
    payload = {}
  ) => {
    const response =
      await adminHttp.post(
        `/api/admin/users/${encodeURIComponent(
          id
        )}/reset-password`,
        payload
      );

    return response.data;
  },

  toggleUser: async (
    id
  ) => {
    const response =
      await adminHttp.patch(
        `/api/admin/users/${encodeURIComponent(
          id
        )}/toggle`
      );

    return response.data;
  },

  /* =======================================================
     RESOURCES
     ======================================================= */

  listResources: async (
    params = {}
  ) => {
    const response =
      await adminHttp.get(
        "/api/resources",
        {
          params,
        }
      );

    return response.data;
  },

  getResource: async (
    id
  ) => {
    const response =
      await adminHttp.get(
        `/api/resources/${encodeURIComponent(
          id
        )}`
      );

    return response.data;
  },

  createResource: async (
    payload
  ) => {
    const response =
      await adminHttp.post(
        "/api/resources",
        payload
      );

    return response.data;
  },

  updateResource: async (
    id,
    payload
  ) => {
    const response =
      await adminHttp.put(
        `/api/resources/${encodeURIComponent(
          id
        )}`,
        payload
      );

    return response.data;
  },

  deleteResource: async (
    id
  ) => {
    const response =
      await adminHttp.delete(
        `/api/resources/${encodeURIComponent(
          id
        )}`
      );

    return response.data;
  },

  starResource: async (
    id,
    starred = true
  ) => {
    const response =
      await adminHttp.patch(
        `/api/resources/${encodeURIComponent(
          id
        )}/star`,
        {
          starred,
        }
      );

    return response.data;
  },

  /* =======================================================
     CATEGORIES
     ======================================================= */

  listCategories: async () => {
    const response =
      await adminHttp.get(
        "/api/categories"
      );

    return response.data;
  },

  /* =======================================================
     ACTIVITY
     ======================================================= */

  listActivity: async (
    params = {}
  ) => {
    const response =
      await adminHttp.get(
        "/api/activity",
        {
          params,
        }
      );

    return response.data;
  },

  /* =======================================================
     STATS
     ======================================================= */

  getStats: async () => {
    const response =
      await adminHttp.get(
        "/api/stats"
      );

    return response.data;
  },

  /* =======================================================
     GENERIC GET
     ======================================================= */

  get: async (
    url,
    config = {}
  ) => {
    const response =
      await adminHttp.get(
        url,
        config
      );

    return response.data;
  },

  /* =======================================================
     GENERIC POST
     ======================================================= */

  post: async (
    url,
    payload = {},
    config = {}
  ) => {
    const response =
      await adminHttp.post(
        url,
        payload,
        config
      );

    return response.data;
  },

  /* =======================================================
     GENERIC PUT
     ======================================================= */

  put: async (
    url,
    payload = {},
    config = {}
  ) => {
    const response =
      await adminHttp.put(
        url,
        payload,
        config
      );

    return response.data;
  },

  /* =======================================================
     GENERIC PATCH
     ======================================================= */

  patch: async (
    url,
    payload = {},
    config = {}
  ) => {
    const response =
      await adminHttp.patch(
        url,
        payload,
        config
      );

    return response.data;
  },

  /* =======================================================
     GENERIC DELETE
     ======================================================= */

  delete: async (
    url,
    config = {}
  ) => {
    const response =
      await adminHttp.delete(
        url,
        config
      );

    return response.data;
  },
};

/* =========================================================
   EXPORT STORAGE KEYS
   ========================================================= */

export {
  ADMIN_TOKEN_KEY,
  ADMIN_USER_KEY,
};